import React from "react";

import type { ButtonProps as ButtonPropsChakra, FlexProps, HeadingProps, TextProps } from "@chakra-ui/react";
import { Button, Flex, Heading, Tag, Text } from "@chakra-ui/react";
import { colors } from "../../theme/colors";
import { useAnalytics, useNavigateCustom } from "../../hooks";
import { useScreenSize } from "@telefonica/mistica";

interface ButtonProps extends ButtonPropsChakra {
	title: string;
	link?: string;
	newTab?: boolean;
	external?: boolean;
}

interface IBannerProps extends FlexProps {
	info: {
		heading: HeadingProps;
		description?: TextProps;
		complementDescription?: TextProps;
		colorSchema?: string;
		infoBox?: FlexProps;
		tag?: string;
		smallTitle?: string;
	};
	button?: ButtonProps;
	mobileImg: string;
	desktopImg: string;
	taggingStep?: string;
	colorOverlay?: React.ReactNode;
	applyDegrade?: boolean;
}

const Banner = ({
	info,
	button,
	mobileImg,
	desktopImg,
	taggingStep,
	colorOverlay,
	applyDegrade,
	...rest
}: IBannerProps) => {
	const { addAnalyticEvent } = useAnalytics();
	const navigate = useNavigateCustom();
	const { isDesktopOrBigger } = useScreenSize();

	const handleClickButton = () => {
		button?.external || button?.newTab ? window.open(button.link, "_blank") : navigate(button?.link!);
	};

	const handleTriggerTagging = () => {
		if (!taggingStep) {
			return;
		}

		addAnalyticEvent(
			{
				component: "body",
				action: "click:button",
				step: taggingStep,
				description: button!.title,
			},
			{
				applySanitize: true,
			},
		);
	};

	const mobileBackgroundImage = applyDegrade
		? `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 90.02%, rgba(0, 0, 0, 0.7) 100%), url(${mobileImg})`
		: `url(${mobileImg})`;

	return (
		<Flex
			bgSize={"cover"}
			h={"326px"}
			backgroundImage={{ base: mobileBackgroundImage, lg: `url(${desktopImg})` }}
			bgPosition={{ base: "right center", sm: "center top", xl: "right top" }}
			bgRepeat="no-repeat"
			borderRadius={"16px"}
			w={"100%"}
			justifyContent={"space-between"}
			alignItems={{ base: "flex-end", lg: "center" }}
			position={"relative"}
			boxSizing={"border-box"}
			overflow="hidden"
			{...rest}
		>
			<Flex
				m={{ base: "24px", lg: "0 40px" }}
				direction={"column"}
				maxW={{ base: "279px", lg: "460px" }}
				gap={"24px"}
				position={"absolute"}
				zIndex={3}
				{...info.infoBox}
			>
				<Flex direction={"column"} gap={{ base: "8px", lg: "16px" }}>
					{isDesktopOrBigger && info.smallTitle && (
						<Text fontSize={"14px"} mb={6} fontWeight={"500"} lineHeight={"20px"} color={"white"}>
							{info.smallTitle}
						</Text>
					)}

					{info.tag && (
						<Tag borderRadius={"999px"} bg={"purple.100"} w={"fit-content"}>
							<Text fontSize={"14px"} fontWeight={"500"} lineHeight={"20px"} color={"purple.500"}>
								{info.tag}
							</Text>
						</Tag>
					)}
					<Heading
						fontFamily="VivoTypeFamily"
						fontWeight={"500"}
						fontSize={{ base: "24px", lg: "40px" }}
						lineHeight={{ base: "32px", lg: "48px" }}
						color={info.colorSchema ?? "white"}
						sx={{
							filter: `drop-shadow(0px 0px 15px ${colors.shadow[600]})`,
						}}
						{...info.heading}
					/>
					{info.description && (
						<Text
							fontFamily="VivoTypeFamily"
							fontWeight={"300"}
							fontSize={{ base: "16px", lg: "28px" }}
							lineHeight={{ base: "24px", lg: "32px" }}
							color={info.colorSchema ?? "white"}
							sx={{
								filter: `drop-shadow(0px 0px 15px ${colors.shadow[600]})`,
							}}
							{...info.description}
						/>
					)}
				</Flex>

				{button && (
					<Button
						fontSize={{ base: "16px", lg: "18px" }}
						fontWeight={"500"}
						fontFamily="VivoType"
						lineHeight={"24px"}
						size={"md"}
						w={"fit-content"}
						onClick={() => {
							handleTriggerTagging();
							handleClickButton();
						}}
						_focus={{ outline: "none" }}
						_active={{ border: "none" }}
						sx={{ WebkitTapHighlightColor: "transparent" }}
						{...button}
					>
						{button.title}
					</Button>
				)}
			</Flex>
			{colorOverlay}
		</Flex>
	);
};

export default Banner;
